import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api/client';
import PanelCard from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import CopyButton from '../../components/ui/CopyButton';
import HwidInfoModal from '../../components/ui/HwidInfoModal';
import { roleBadgeClass } from '../../constants/roles';
import { ACTION_LABELS } from '../owner/constants';
import PurchaseHistorySection from './PurchaseHistorySection';

/** `2026-09-12T06:49:00.000Z` → `2026-09-12 06:49` (UTC, ahogy a szerver küldi). */
const stamp = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');
const euro = (n) => `€${Number(n || 0).toFixed(2)}`;

function hours(seconds) {
  if (!seconds) return '0h';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

function Row({ label, children, mono }) {
  return (
    <div className="flex justify-between items-start gap-3 py-2 border-b border-white/[0.05] last:border-0 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-gray-200 text-right break-all min-w-0 ${mono ? 'font-mono text-xs' : ''}`}>
        {children}
      </span>
    </div>
  );
}

function Stat({ label, value, tone = 'text-white' }) {
  return (
    <div className="card p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`font-display text-xl font-bold tabular-nums mt-1 ${tone}`}>{value}</p>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-gray-600 surface-sunken px-4 py-3">{children}</p>;
}

/** Hol áll ez a fiók az audit bejegyzésben: ő csinálta, vele csinálták, vagy magára. */
const RELATION = {
  self:   { label: 'self',   tone: 'bg-white/[0.05] text-gray-400 border-white/[0.10]' },
  actor:  { label: 'did',    tone: 'bg-brand-500/12 text-brand-200 border-brand-400/25' },
  target: { label: 'on them', tone: 'bg-amber-500/12 text-amber-300 border-amber-500/25' },
};

/**
 * Egy fiók TELJES profilja a stábnak — minden, ami róla tudható, egy oldalon.
 *
 * Egyetlen kérés hozza (`/admin/users/:id/profile`), hogy a szekciók egymással
 * konzisztens pillanatképet mutassanak, ne külön-külön, eltérő időpontban
 * betöltött darabokat.
 *
 * Az AUDIT LOG, az IP-címek és a BAN-NAPLÓ csak ownernek jön le: ezek a
 * felületen eddig is owner-only-k voltak, és a profil oldal ezt a határt nem
 * tágítja. Adminnál a helyükön egy jelzés áll, hogy a hiányuk ne tűnjön üres
 * adatnak.
 */
export default function UserProfilePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hwidOpen, setHwidOpen] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api.get(`/admin/users/${id}/profile`)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.status === 404 ? 'User not found.' : 'Failed to load the profile.'));
  }, [id]);

  useEffect(() => { setData(null); load(); }, [load]);

  if (error) {
    return (
      <div className="page-container max-w-6xl">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-white">← Admin Panel</Link>
        <p className="mt-10 text-center text-red-300">{error}</p>
      </div>
    );
  }
  if (!data) return <LoadingSpinner fullPage label="Loading profile…" />;

  const a = data.account;
  const p = data.purchases;
  const deleted = a.email?.endsWith('@deleted.invalid');
  const lifetime = p.user.lifetime;
  const subActive = a.expires_at && !a.is_frozen && new Date(a.expires_at) > new Date();

  const status = deleted ? ['Deleted', 'badge-inactive']
    : a.is_banned ? ['Banned', 'badge-banned']
    : lifetime ? ['Lifetime', 'badge-active']
    : a.is_frozen ? ['Frozen', 'badge-frozen']
    : subActive ? ['Active sub', 'badge-active']
    : ['No sub', 'badge-inactive'];

  return (
    <div className="page-container max-w-6xl">
      <Link to="/admin" className="text-sm text-gray-500 hover:text-white">← Admin Panel</Link>

      {/* ── Fejléc ── */}
      <header className="mt-4 mb-6 flex items-start gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-brand-gradient border border-brand-400/25
                        flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
          {a.username[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold text-white tracking-tight break-all">{a.username}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${roleBadgeClass(a.role)}`}>
              {a.role}
            </span>
            <span className={status[1]}>{status[0]}</span>
            <span className="text-xs text-gray-500">#{a.id} · registered {stamp(a.created_at)} UTC</span>
          </div>
          {a.is_banned === 1 && !deleted && a.ban_reason && (
            <p className="mt-2 text-sm text-red-300">Ban reason: {a.ban_reason}</p>
          )}
        </div>
        <button onClick={load} className="btn-secondary text-sm px-3 py-1.5 ml-auto">Refresh</button>
      </header>

      {/* ── Összegző számok ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Spent" value={euro(p.summary.spent)} tone="text-emerald-300" />
        <Stat label="Paid orders" value={p.summary.delivered} />
        <Stat label="Farming" value={hours(data.farm.seconds)} tone="text-brand-200" />
        <Stat label="Referred" value={`${data.referrals.referred.length} (+${data.referrals.daysEarned}d)`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── Bal: a hosszú listák ── */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <PanelCard title="Purchases" subtitle="Shop orders and every key activation. Times are UTC.">
            <PurchaseHistorySection data={p} />
          </PanelCard>

          <PanelCard title="Referrals" subtitle="Who brought this account, and who this account brought.">
            <div className="space-y-4">
              <div className="text-sm">
                {data.referrals.referredBy ? (
                  <p className="text-gray-300">
                    Joined through <span className="text-brand-200">{data.referrals.referredBy.referrer_username}</span>'s
                    code on {stamp(data.referrals.referredBy.created_at)} — they got +{data.referrals.referredBy.days_granted} days.
                  </p>
                ) : (
                  <p className="text-gray-600">Did not use anyone's referral code.</p>
                )}
                <p className="text-gray-500 mt-1">
                  Own code: <span className="font-mono text-gray-300">{a.referral_code || '—'}</span>
                  {data.referrals.uncreditedCount > 0 && (
                    <span className="text-amber-300">
                      {' '}· {data.referrals.uncreditedCount} order(s) used this code without a credit
                    </span>
                  )}
                </p>
              </div>

              {data.referrals.referred.length === 0 ? (
                <Empty>Nobody has redeemed this account's code yet.</Empty>
              ) : (
                <div className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.05]">
                  {data.referrals.referred.map(r => (
                    <div key={r.order_id} className="px-4 py-2.5 text-sm flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xs text-gray-400">{stamp(r.created_at)}</span>
                      <Link to={`/admin/users/${r.user_id}`} className="text-gray-200 hover:text-white">{r.username}</Link>
                      <span className="text-gray-500 text-xs">{r.plan_label || '—'} · {euro(r.final_price)}</span>
                      <span className="ml-auto text-emerald-300 font-semibold tabular-nums">+{r.days_granted}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PanelCard>

          <PanelCard title="Invites" subtitle="Invite requests, and the invite codes this account owns.">
            <div className="space-y-4 text-sm">
              <p className={data.invites.usedInvite ? 'text-gray-300' : 'text-gray-600'}>
                {data.invites.usedInvite
                  ? <>Registered with invite <span className="font-mono">{data.invites.usedInvite.code}</span>
                      {data.invites.usedInvite.owner && <> from {data.invites.usedInvite.owner}</>}.</>
                  : 'Did not register with an invite code.'}
              </p>

              {data.invites.requests.length === 0 ? (
                <Empty>No invite requests.</Empty>
              ) : (
                <div className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.05]">
                  {data.invites.requests.map(r => (
                    <div key={r.id} className="px-4 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-gray-400">{stamp(r.created_at)}</span>
                        <span className="text-xs capitalize text-gray-300">{r.status}</span>
                        {r.reviewed_by && (
                          <span className="text-xs text-gray-600">by {r.reviewed_by} {stamp(r.reviewed_at)}</span>
                        )}
                      </div>
                      <p className="text-gray-400 mt-1 break-words">{r.reason}</p>
                    </div>
                  ))}
                </div>
              )}

              {data.invites.codes.length > 0 && (
                <div className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.05]">
                  {data.invites.codes.map(c => (
                    <div key={c.code} className="px-4 py-2.5 flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xs text-brand-200">{c.code}</span>
                      {c.used_by
                        ? <span className="text-xs text-gray-400">used by{' '}
                            <Link to={`/admin/users/${c.used_by_id}`} className="text-gray-200 hover:text-white">{c.used_by}</Link>
                            {' '}{stamp(c.used_at)}</span>
                        : <span className="text-xs text-gray-600">unused</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PanelCard>

          {/* ── Owner-only: audit log ── */}
          {data.audit ? (
            <PanelCard
              flush
              title="Audit log"
              subtitle={
                data.audit.total > data.audit.rows.length
                  ? `The ${data.audit.rows.length} most recent of ${data.audit.total} entries — everything this account did, and everything done to it.`
                  : `${data.audit.total} entries — everything this account did, and everything done to it.`
              }
            >
              {data.audit.rows.length === 0 ? (
                <div className="px-5 py-4"><Empty>No audit entries.</Empty></div>
              ) : (
                <div className="divide-y divide-white/[0.05]">
                  {data.audit.rows.map(l => {
                    const rel = RELATION[l.relation] || RELATION.actor;
                    const details = l.details && Object.keys(l.details).length ? JSON.stringify(l.details) : null;
                    return (
                      <div key={l.id} className="px-5 py-2.5 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-gray-400">{stamp(l.created_at)}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${rel.tone}`}>{rel.label}</span>
                          <span className="text-gray-200">{ACTION_LABELS[l.action] || l.action}</span>
                          {l.relation === 'target' && (
                            <span className="text-xs text-gray-500">by {l.actor_name}</span>
                          )}
                          {l.ip_address && <span className="ml-auto font-mono text-[11px] text-gray-600">{l.ip_address}</span>}
                        </div>
                        {details && (
                          <p className="mt-1 font-mono text-[11px] text-gray-600 break-all">{details}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </PanelCard>
          ) : (
            <div className="card p-5 text-sm text-gray-500">
              The audit log, IP addresses and ban detections for this account are visible to the owner only.
            </div>
          )}
        </div>

        {/* ── Jobb: a fiók tényei ── */}
        <div className="space-y-6 min-w-0">
          <PanelCard title="Account">
            <div className="surface-sunken px-3.5">
              <Row label="Email">{a.email}</Row>
              <Row label="Email verified">{a.email_verified_at ? stamp(a.email_verified_at) : 'Not verified'}</Row>
              <Row label="ToS accepted">{stamp(a.tos_accepted_at)}</Row>
              {a.invited_by_name && <Row label="Invited by">{a.invited_by_name}</Row>}
              <Row label="Active logins">{data.sessions.activeLogins}</Row>
              <Row label="Last login">{stamp(data.sessions.lastLogin)}</Row>
            </div>
          </PanelCard>

          <PanelCard title="Subscription">
            <div className="surface-sunken px-3.5">
              {lifetime ? (
                <Row label="License">Lifetime ({a.role})</Row>
              ) : (
                <>
                  <Row label="Status">{!a.expires_at ? 'No subscription' : a.is_frozen ? 'Frozen' : subActive ? 'Active' : 'Expired'}</Row>
                  {a.plan_label && <Row label="Plan">{a.plan_label}</Row>}
                  {a.expires_at && <Row label="Expires">{stamp(a.expires_at)}</Row>}
                  {a.frozen_at && <Row label="Frozen at">{stamp(a.frozen_at)}</Row>}
                </>
              )}
            </div>
          </PanelCard>

          <PanelCard title="Discord">
            <div className="surface-sunken px-3.5">
              {a.discord_id ? (
                <>
                  <Row label="Name">
                    {a.discord_username}
                    {a.discord_handle && a.discord_handle !== a.discord_username && (
                      <span className="text-gray-500"> · @{a.discord_handle}</span>
                    )}
                  </Row>
                  <Row label="ID" mono>
                    <span className="inline-flex items-center gap-1.5">
                      {a.discord_id}<CopyButton value={a.discord_id} variant="icon" title="Copy Discord ID" />
                    </span>
                  </Row>
                  <Row label="Linked">{stamp(a.discord_linked_at)}</Row>
                </>
              ) : (
                <Row label="Status"><span className="text-gray-600">Not linked</span></Row>
              )}
            </div>
          </PanelCard>

          <PanelCard
            title="Device"
            actions={<button onClick={() => setHwidOpen(true)} className="btn-secondary text-xs px-3 py-1.5">HWID details</button>}
          >
            <div className="surface-sunken px-3.5">
              <Row label="HWID" mono>{a.hwid || '—'}</Row>
              <Row label="Last seen">{stamp(a.hwid_last_seen)}</Row>
              {a.hwid_rebind_count > 0 && <Row label="Auto-rebinds">{a.hwid_rebind_count}×</Row>}
            </div>
          </PanelCard>

          <PanelCard title="Farming">
            <div className="surface-sunken px-3.5">
              <Row label="Runs">{data.farm.runs}</Row>
              <Row label="Days farmed">{data.farm.days}</Row>
              <Row label="Total time">{hours(data.farm.seconds)}</Row>
              <Row label="First run">{stamp(data.farm.firstAt)}</Row>
              <Row label="Last activity">{stamp(data.farm.lastAt)}</Row>
              <Row label="Leaderboard">{a.leaderboard_opt_in ? 'Opted in' : 'Not listed'}</Row>
            </div>
          </PanelCard>

          {data.ips && (
            <PanelCard title="IP addresses" subtitle="From this account's own audit entries.">
              {data.ips.length === 0 ? <Empty>None recorded.</Empty> : (
                <div className="surface-sunken px-3.5">
                  {data.ips.map(ip => (
                    <Row key={ip.ip_address} label={<span className="font-mono text-xs text-gray-300">{ip.ip_address}</span>}>
                      <span className="text-xs text-gray-500">{ip.count}× · last {stamp(ip.last_at)}</span>
                    </Row>
                  ))}
                </div>
              )}
            </PanelCard>
          )}

          {data.banLogs && (
            <PanelCard
              title="Ban detections"
              subtitle={data.banLogs.total > data.banLogs.rows.length
                ? `The ${data.banLogs.rows.length} most recent of ${data.banLogs.total}.`
                : 'Matched by this account, or by its current HWID.'}
            >
              {data.banLogs.rows.length === 0 ? <Empty>No detections.</Empty> : (
                <div className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.05]">
                  {data.banLogs.rows.map(b => (
                    <div key={b.id} className="px-3.5 py-2.5 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-gray-400">{stamp(b.created_at)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                          b.matched === 'account'
                            ? 'bg-red-500/12 text-red-300 border-red-500/25'
                            : 'bg-amber-500/12 text-amber-300 border-amber-500/25'
                        }`}>{b.matched === 'account' ? 'this account' : 'same HWID'}</span>
                      </div>
                      <p className="text-gray-300 mt-1 break-words">{b.reason}</p>
                      {b.processes?.length > 0 && (
                        <p className="text-[11px] text-gray-600 mt-0.5">{b.processes.length} process(es) reported</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </PanelCard>
          )}
        </div>
      </div>

      {hwidOpen && <HwidInfoModal user={a} onClose={() => setHwidOpen(false)} />}
    </div>
  );
}
